import React, {useMemo} from 'react';
import {observer} from 'mobx-react';
import s from '../../../s';
import {isNullOrWhiteSpace} from '../../../../util';
import {EP_INHERITED_LINE_HEIGHT, sanitizeEPContent, transformPtToCqh} from '../ep-content-utils';
import {EPTemplateLineInputSpec, EPTemplateOption, epLineFailsRegex, epLineSpec, epPlaceholderIsSyntax} from './ep-templates';

type TemplateLine = {
    number: number;
    title: string;
    placeholder?: string;
    spec?: EPTemplateLineInputSpec | null;
    // ep_lines[].optional: the shopper may leave the line blank.
    optional?: boolean;
    // ep_lines[].plate: display-only grouping hint (2 = listed under the second plate).
    plate?: number;
};

type Props = {
    visibleTemplateLines: TemplateLine[];
    onTemplateLineChange: (lineNumber: number, value: string) => void;
    templates?: EPTemplateOption[];
    selectedTemplateID?: number;
    onTemplateSelectChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const formatIn = (v: number): string => (Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000));

// One plate's preview box: the resolved (sanitized, pt -> cqh) HTML on the plate
// background, sized by aspect ratio so the content's cqh font units resolve. Also the
// inst-plate module's preview and proof (empty content = the blank plate in its finish),
// which pass maxWidthPx so each plate size shows at its own width.
export const PlatePreview = observer(({content, width, height, heightPt, className, maxWidthPx}: {content: string; width: number; height: number; heightPt?: number; className: string; maxWidthPx?: number}) => {
    const sanitizedContent = useMemo(() => transformPtToCqh(sanitizeEPContent(content)), [content]);

    // Portrait plates (e.g. 2.5" x 3.5" marathon) size from height instead: full width
    // would make them enormous, so cap at 30vh and let aspect-ratio derive the width
    // (the max-height transfers to a max-width through the aspect ratio); margin auto
    // keeps the narrower box centered.
    const isPortrait = height > width;
    const previewSizing: React.CSSProperties = isPortrait ? {width: 'auto', height: '100%', maxHeight: '30vh', marginInline: 'auto'} : {width: '100%', height: 'auto'};

    return (
        <div
            className={className}
            style={
                {
                    // Template mode: full-width preview with proportional (auto) height
                    // (flipped for portrait plates — see isPortrait). The aspect-ratio
                    // gives the box a definite block size so the content's cqh font
                    // units still resolve — with container-type: size a bare height:auto
                    // would collapse the size-contained box to 0 and hide the plate text.
                    ...previewSizing,
                    ...(maxWidthPx ? {maxWidth: maxWidthPx} : {}),
                    aspectRatio: `${width} / ${height}`,
                    backgroundImage: s.ps.css?.ep_backgroundImage,
                    backgroundSize: s.ps.css?.ep_backgroundSize,
                    color: s.ps.css?.ep_color,
                    lineHeight: EP_INHERITED_LINE_HEIGHT,
                    containerType: 'size',
                    '--ep-h-pt': heightPt || Math.round(height * 72),
                } as React.CSSProperties
            }
        >
            <div
                style={{
                    padding: s.ps.css?.ep_padding || '0',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
                dangerouslySetInnerHTML={{__html: sanitizedContent}}
            />
        </div>
    );
});

// One labelled input (or dropdown) row of a template line, bound to s.ps.ep{N}. Used by
// EPTemplate and by the inst-plate module.
export const EPTemplateLineRow = observer(({line, onTemplateLineChange}: {line: TemplateLine; onTemplateLineChange: (lineNumber: number, value: string) => void}) => {
    // The line's input control (epLineSpec): settings-driven templates carry it
    // directly from the backend's per-line ep_lines settings (field type,
    // validator regex, dropdown options); legacy lines fall back to the
    // placeholder syntax ("dd::a:b", "n4::2022", "t255::Facility",
    // "rgx(\d{4}):2022"). A dropdown starts on a disabled placeholder option
    // with a blank value unless the line's `default` filled it (ep-template-apply
    // applyTemplateLineDefaults); a blank line is "missing" by the existing rules
    // and highlights after a failed Add to Cart like any other.
    const inputSpec = epLineSpec(line);

    // A line is "in violation" once an add-to-cart attempt failed validation
    // (ep_template_show_errors) and it is still blank (unless optional) or fails
    // its pattern. Only regex-kind lines additionally highlight live while their
    // text doesn't match — numbers/text patterns (exact digit count / length cap)
    // fail on every keystroke of a partially typed value, so flagging them live
    // would show the error state during normal typing.
    const lineValue = s.ps[`ep${line.number}`] || '';
    const patternFails = epLineFailsRegex(inputSpec, lineValue);
    const blankViolation = isNullOrWhiteSpace(lineValue) && !line.optional;
    const showError = (inputSpec?.kind === 'regex' && patternFails) || (s.ui.ep_template_show_errors && (blankViolation || patternFails));
    const dropdownOptions = inputSpec?.kind === 'dropdown' ? inputSpec.options : null;
    const dropdownValue = dropdownOptions ? s.ps[`ep${line.number}`] || '' : '';
    // A value saved before the template's options changed may no longer be listed;
    // keep it selectable so the plate still shows what the customer chose.
    const dropdownOptionsWithValue = dropdownOptions && dropdownValue !== '' && !dropdownOptions.includes(dropdownValue) ? [...dropdownOptions, dropdownValue] : dropdownOptions;

    const isNumeric = inputSpec?.kind === 'numeric';
    // rgx lines get the backend's 255-char ep-value cap — the pattern itself
    // decides what's acceptable, so don't second-guess it with the 50 default.
    const inputMaxLength = inputSpec?.kind === 'numeric' ? inputSpec.maxDigits : inputSpec?.kind === 'text' ? inputSpec.maxLength : inputSpec?.kind === 'regex' ? 255 : 50;
    // The line's ghost text: its spec's (the settings ph, or the n/t/rgx syntax's text), else a
    // plain placeholder; recognized-but-unusable syntax ("dd::" with no options, "n0::", an rgx()
    // that doesn't compile) never leaks to the customer. An input shows it as its placeholder, a
    // dropdown as its first, disabled option; without one, "Enter {title}" / "Select {title}".
    const rawIsSyntax = epPlaceholderIsSyntax(line.placeholder);
    const ghost = inputSpec?.placeholder || (line.placeholder && !rawIsSyntax ? line.placeholder : '');

    return (
        <div className="ep-template-line">
            <label
                htmlFor={`ep-template-${line.number}`}
                className="ep-template-label"
            >
                {line.title}
                {line.optional && <span className="ep-template-optional"> (optional)</span>}:
                {showError && (
                    <span
                        className="ep-template-required"
                        aria-hidden="true"
                    >
                        *
                    </span>
                )}
            </label>
            {dropdownOptionsWithValue ? (
                <select
                    id={`ep-template-${line.number}`}
                    className={`ep-template-input ep-template-select form-control ib input-qty${showError ? ' ep-template-input-error' : ''}`}
                    aria-label={line.title}
                    aria-invalid={showError || undefined}
                    value={dropdownValue}
                    onChange={e => {
                        onTemplateLineChange(line.number, e.target.value);
                    }}
                >
                    <option
                        value=""
                        disabled
                    >
                        {ghost || `Select ${line.title}`}
                    </option>
                    {dropdownOptionsWithValue.map(opt => (
                        <option
                            key={opt}
                            value={opt}
                        >
                            {opt}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    type="text"
                    id={`ep-template-${line.number}`}
                    className={`ep-template-input form-control ib input-qty${showError ? ' ep-template-input-error' : ''}`}
                    maxLength={inputMaxLength}
                    inputMode={isNumeric ? 'numeric' : undefined}
                    aria-label={line.title}
                    aria-invalid={showError || undefined}
                    placeholder={ghost || `Enter ${line.title.toLowerCase()}`}
                    autoComplete="off"
                    value={s.ps[`ep${line.number}`] || ''}
                    onChange={e => {
                        onTemplateLineChange(line.number, isNumeric ? e.target.value.replace(/\D/g, '') : e.target.value);
                    }}
                />
            )}
        </div>
    );
});

export const EPTemplate = observer(({visibleTemplateLines, onTemplateLineChange, templates, selectedTemplateID, onTemplateSelectChange}: Props) => {
    const epWidth = s.ps.EPWidth || 3;
    const epHeight = s.ps.EPHeight || 2;
    const hasPlate2 = s.ps.EP2Width > 0 && s.ps.EP2Height > 0 && !isNullOrWhiteSpace(s.ps.EP2Content);

    // Hide the template dropdown when there is nothing to choose between (a single template).
    const showPicker = !!templates && templates.length > 1 && !!onTemplateSelectChange;

    // Inputs are grouped per plate only when the template hints at it (ep_lines[].plate
    // = 2 on at least one line) and there really is a second plate; otherwise one flat
    // list, exactly as before.
    const grouped = hasPlate2 && visibleTemplateLines.some(l => l.plate === 2);
    const lineGroups: Array<{heading: string | null; lines: TemplateLine[]}> = grouped
        ? [
              {heading: `Main plate (${formatIn(epWidth)}" x ${formatIn(epHeight)}")`, lines: visibleTemplateLines.filter(l => l.plate !== 2)},
              {heading: `Second plate (${formatIn(s.ps.EP2Width)}" x ${formatIn(s.ps.EP2Height)}")`, lines: visibleTemplateLines.filter(l => l.plate === 2)},
          ].filter(g => g.lines.length > 0)
        : [{heading: null, lines: visibleTemplateLines}];

    return (
        <div className="ep-template">
            {showPicker && (
                <div className="ep-template-picker">
                    <label
                        htmlFor="ep-template-select"
                        className="ep-template-picker-label"
                    >
                        Template:
                    </label>
                    <select
                        id="ep-template-select"
                        name="ep-template-select"
                        aria-label="Select engraved plate template"
                        value={selectedTemplateID ?? templates![0]?.id ?? ''}
                        onChange={onTemplateSelectChange}
                    >
                        {templates!.map(t => (
                            <option
                                key={t.id}
                                value={t.id}
                            >
                                {t.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <PlatePreview
                className="ep-template-preview"
                content={s.ps.EPContent || ''}
                width={epWidth}
                height={epHeight}
                heightPt={s.ps.css?.ep_plateHeightPt}
            />
            {/* Second plate of a two-plate template: its own preview box (own aspect
                ratio, same background/ink colour), sized from the template's plate2. */}
            {hasPlate2 && (
                <PlatePreview
                    className="ep-template-preview ep-template-preview-2"
                    content={s.ps.EP2Content}
                    width={s.ps.EP2Width}
                    height={s.ps.EP2Height}
                />
            )}
            {lineGroups.map((group, gi) => (
                <React.Fragment key={group.heading || gi}>
                    {group.heading && <div className="ep-template-group-heading">{group.heading}</div>}
                    {group.lines.map(line => (
                        <EPTemplateLineRow
                            key={line.number}
                            line={line}
                            onTemplateLineChange={onTemplateLineChange}
                        />
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
});
